import type { WorldBuilderResult } from '@creanizer/types'

/**
 * 世界観の完全なコンテキストテキストを生成する
 * 全ジェネレータで統一された詳細な世界観情報を提供
 */
export function generateWorldContextText(world: WorldBuilderResult): string {
  let contextText = `【世界名】${world.name}\n\n`
  contextText += `【概要】${world.summary}\n\n`
  contextText += `【コンセプト】${world.concept}\n\n`
  contextText += `【ジャンル】${world.genres.join('、')}\n\n`
  contextText += `【世界規模】${world.worldScale}\n\n`
  
  // パラメータ
  contextText += `【世界パラメータ】\n`
  contextText += `- 魔法レベル: ${world.parameters.magicLevel}/10\n`
  contextText += `- 科学レベル: ${world.parameters.scienceLevel}/10\n`
  contextText += `- 文明レベル: ${world.parameters.civilizationLevel}/10\n`
  contextText += `- 危険度: ${world.parameters.dangerLevel}/10\n`
  contextText += `- 神秘性: ${world.parameters.mysteryLevel}/10\n`
  contextText += `- 複雑性: ${world.parameters.complexityLevel}/10\n`
  contextText += `- 独自性: ${world.parameters.uniquenessLevel}/10\n`
  contextText += `- 混沌度: ${world.parameters.chaosLevel}/10\n\n`
  
  // ユニークな特徴
  if (world.uniqueFeatures) {
    contextText += `【特徴的な要素】\n`
    contextText += `- 特徴: ${world.uniqueFeatures.signature}\n`
    if (world.uniqueFeatures.laws?.length > 0) {
      contextText += `- 法則: ${world.uniqueFeatures.laws.join('、')}\n`
    }
    if (world.uniqueFeatures.mysteries?.length > 0) {
      contextText += `- 謎: ${world.uniqueFeatures.mysteries.join('、')}\n`
    }
    if (world.uniqueFeatures.innovations?.length > 0) {
      contextText += `- 革新: ${world.uniqueFeatures.innovations.join('、')}\n`
    }
    if (world.uniqueFeatures.paradoxes?.length > 0) {
      contextText += `- パラドックス: ${world.uniqueFeatures.paradoxes.join('、')}\n`
    }
    contextText += '\n'
  }
  
  // 時代設定
  if (world.details?.era) {
    contextText += `【時代設定】\n`
    contextText += `- 時代名: ${world.details.era.name}\n`
    contextText += `- 説明: ${world.details.era.description}\n`
    contextText += `- 技術レベル: ${world.details.era.technologyLevel}\n`
    contextText += `- 社会構造: ${world.details.era.socialStructure}\n\n`
  }
  
  // 地理
  if (world.details?.geography) {
    contextText += `【地理】\n`
    contextText += `- 説明: ${world.details.geography.description}\n`
    contextText += `- 気候: ${world.details.geography.climate}\n`
    if (world.details.geography.terrain?.length > 0) {
      contextText += `- 地形: ${world.details.geography.terrain.join('、')}\n`
    }
    if (world.details.geography.naturalResources?.length > 0) {
      contextText += `- 天然資源: ${world.details.geography.naturalResources.join('、')}\n`
    }
    if (world.details.geography.notableLocations?.length > 0) {
      contextText += `- 重要な場所: ${world.details.geography.notableLocations.join('、')}\n`
    }
    contextText += '\n'
  }
  
  // 魔法体系
  if (world.details?.magicSystem?.exists) {
    contextText += `【魔法体系】\n`
    contextText += `- 説明: ${world.details.magicSystem.description}\n`
    if (world.details.magicSystem.rules?.length > 0) {
      contextText += `- ルール: ${world.details.magicSystem.rules.join('、')}\n`
    }
    if (world.details.magicSystem.limitations?.length > 0) {
      contextText += `- 制限: ${world.details.magicSystem.limitations.join('、')}\n`
    }
    if (world.details.magicSystem.practitioners?.length > 0) {
      contextText += `- 使い手: ${world.details.magicSystem.practitioners.join('、')}\n`
    }
    contextText += '\n'
  }
  
  // 技術
  if (world.details?.technology) {
    contextText += `【技術】\n`
    contextText += `- レベル: ${world.details.technology.level}\n`
    if (world.details.technology.keyTechnologies?.length > 0) {
      contextText += `- 主要技術: ${world.details.technology.keyTechnologies.join('、')}\n`
    }
    if (world.details.technology.limitations?.length > 0) {
      contextText += `- 制限: ${world.details.technology.limitations.join('、')}\n`
    }
    if (world.details.technology.uniqueAspects?.length > 0) {
      contextText += `- 独自の側面: ${world.details.technology.uniqueAspects.join('、')}\n`
    }
    contextText += '\n'
  }
  
  
  // 文化（複数ある場合があるので詳細に）
  if (world.details?.cultures && world.details.cultures.length > 0) {
    contextText += `【文化】\n`
    world.details.cultures.forEach(culture => {
      contextText += `◆ ${culture.name}\n`
      contextText += `  - 説明: ${culture.description}\n`
      if (culture.values?.length > 0) {
        contextText += `  - 価値観: ${culture.values.join('、')}\n`
      }
      if (culture.customs?.length > 0) {
        contextText += `  - 習慣: ${culture.customs.join('、')}\n`
      }
      if (culture.taboos?.length > 0) {
        contextText += `  - タブー: ${culture.taboos.join('、')}\n`
      }
      if (culture.language) {
        contextText += `  - 言語: ${culture.language}\n`
      }
      if (culture.governance) {
        contextText += `  - 統治: ${culture.governance}\n`
      }
      if (culture.architecture) {
        contextText += `  - 建築: ${culture.architecture}\n`
      }
      if (culture.clothing) {
        contextText += `  - 服装: ${culture.clothing}\n`
      }
    })
    contextText += '\n'
  }
  
  // 脅威と敵対勢力（エネミー生成に特に重要）
  if (world.details?.threats) {
    contextText += `【脅威・敵対勢力】\n`
    if (world.details.threats.hostileForces && world.details.threats.hostileForces.length > 0) {
      world.details.threats.hostileForces.forEach(force => {
        contextText += `◆ ${force.name}\n`
        contextText += `  - タイプ: ${force.type}\n`
        contextText += `  - 説明: ${force.description}\n`
        contextText += `  - 脅威: ${force.threat}\n`
        if (force.capabilities?.length > 0) {
          contextText += `  - 能力: ${force.capabilities.join('、')}\n`
        }
        if (force.weaknesses && force.weaknesses.length > 0) {
          contextText += `  - 弱点: ${force.weaknesses?.join('、')}\n`
        }
        if (force.samples && force.samples.length > 0) {
          contextText += `  - サンプル: ${force.samples?.join('、')}\n`
        }
      })
    }
    
    if (world.details.threats.worldDangers && world.details.threats.worldDangers.length > 0) {
      contextText += `世界の危険: ${world.details.threats.worldDangers?.join('、')}\n`
    }
    
    if (world.details.threats.conflictSituation) {
      const conflict = world.details.threats.conflictSituation
      if (conflict.currentWars && conflict.currentWars.length > 0) {
        contextText += `現在の戦争:\n`
        conflict.currentWars.forEach(war => {
          contextText += `  - ${war.name}: ${war.cause} (状態: ${war.status})\n`
        })
      }
      if (conflict.tensions?.length > 0) {
        contextText += `緊張状態: ${conflict.tensions.join('、')}\n`
      }
      if (conflict.dangerZones?.length > 0) {
        contextText += `危険地帯: ${conflict.dangerZones.join('、')}\n`
      }
      if (conflict.securityMeasures?.length > 0) {
        contextText += `安全対策: ${conflict.securityMeasures.join('、')}\n`
      }
    }
    contextText += '\n'
  }
  
  
  // タイムライン（歴史的出来事）
  if (world.details?.timeline && world.details.timeline.length > 0) {
    contextText += `【重要な歴史】\n`
    // 全ての歴史的出来事を表示
    world.details.timeline.forEach(event => {
      contextText += `- ${event.year}: ${event.event} (影響度: ${event.impact}, カテゴリ: ${event.category})\n`
    })
    contextText += '\n'
  }
  
  return contextText.trim()
}

/**
 * 世界観の簡易コンテキストテキストを生成する
 * 文字数制限がある場合に使用
 */
export function generateWorldContextSummary(world: WorldBuilderResult): string {
  let contextText = `【世界観】${world.name}\n`
  contextText += `概要: ${world.summary}\n`
  contextText += `時代: ${world.details?.era?.name || '不明'} - ${world.details?.era?.description || ''}\n`
  contextText += `技術レベル: ${world.parameters?.scienceLevel || 5}/10\n`
  contextText += `魔法レベル: ${world.parameters?.magicLevel || 5}/10\n`
  contextText += `文明レベル: ${world.parameters?.civilizationLevel || 5}/10\n`
  contextText += `文化: ${world.details?.cultures?.map(c => c.name).join('、') || '不明'}\n`
  contextText += `脅威: ${world.details?.threats?.hostileForces?.map(f => f.name).join('、') || '不明'}`
  
  return contextText.trim()
}